import React, { createContext, useContext, useEffect, useState, useMemo, ReactNode } from 'react';
import { ConfigProvider } from 'antd';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { ThemeMode, Theme } from '../../styles/theme/theme.types';
import { lightColors, darkColors } from '../../styles/theme/colors';
import { getLightTheme, getDarkTheme } from '../../styles/theme/antdTheme';
import plPL from 'antd/locale/pl_PL';

interface ThemeContextType {
  mode: ThemeMode;
  isDark: boolean;
  setMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'flexmenu-theme-mode';

const getSystemPreference = (): boolean => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }
  return false;
};

const getStoredMode = (): ThemeMode => {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  }
  return 'system';
};

interface ThemeContextProviderProps {
  children: ReactNode;
}

export const ThemeContextProvider: React.FC<ThemeContextProviderProps> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>('system');
  const [systemPrefersDark, setSystemPrefersDark] = useState(getSystemPreference);

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemPrefersDark(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Calculate if dark mode is active
  const isDark = useMemo(() => {
    if (mode === 'system') {
      return systemPrefersDark;
    }
    return mode === 'dark';
  }, [mode, systemPrefersDark]);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;

    if (isDark) {
      root.setAttribute('data-theme', 'dark');
      root.classList.add('dark-mode');
      root.classList.remove('light-mode');
    } else {
      root.setAttribute('data-theme', 'light');
      root.classList.add('light-mode');
      root.classList.remove('dark-mode');
    }

    // Set CSS variables
    const colors = isDark ? darkColors : lightColors;

    root.style.setProperty('--color-background', colors.background);
    root.style.setProperty('--color-background-secondary', colors.backgroundSecondary);
    root.style.setProperty('--color-surface', colors.surface);
    root.style.setProperty('--color-surface-hover', colors.surfaceHover);
    root.style.setProperty('--color-text', colors.text);
    root.style.setProperty('--color-text-secondary', colors.textSecondary);
    root.style.setProperty('--color-text-tertiary', colors.textTertiary);
    root.style.setProperty('--color-text-muted', colors.textMuted);
    root.style.setProperty('--color-text-inverse', colors.textInverse);
    root.style.setProperty('--color-primary', colors.primary);
    root.style.setProperty('--color-primary-hover', colors.primaryHover);
    root.style.setProperty('--color-primary-active', colors.primaryActive);
    root.style.setProperty('--color-border', colors.border);
    root.style.setProperty('--color-border-light', colors.borderLight);
    root.style.setProperty('--color-success', colors.success);
    root.style.setProperty('--color-success-bg', colors.successBg);
    root.style.setProperty('--color-warning', colors.warning);
    root.style.setProperty('--color-warning-bg', colors.warningBg);
    root.style.setProperty('--color-error', colors.error);
    root.style.setProperty('--color-error-bg', colors.errorBg);
    root.style.setProperty('--color-info', colors.info);
    root.style.setProperty('--color-info-bg', colors.infoBg);
    root.style.setProperty('--color-overlay', colors.overlay);
    root.style.setProperty('--color-overlay-light', colors.overlayLight);
  }, [isDark]);

  const setMode = (newMode: ThemeMode) => {
    setModeState(newMode);
    localStorage.setItem(THEME_STORAGE_KEY, newMode);
  };

  const toggleTheme = () => {
    const newMode = isDark ? 'light' : 'dark';
    setMode(newMode);
  };

  const theme: Theme = useMemo(() => ({
    mode,
    colors: isDark ? darkColors : lightColors,
  }), [mode, isDark]);

  const antdTheme = useMemo(() => {
    return isDark ? getDarkTheme() : getLightTheme();
  }, [isDark]);

  const contextValue = useMemo(() => ({
    mode,
    isDark,
    setMode,
    toggleTheme,
  }), [mode, isDark]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <ConfigProvider theme={antdTheme} locale={plPL}>
        <StyledThemeProvider theme={theme}>
          {children}
        </StyledThemeProvider>
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeContextProvider');
  }
  return context;
};
